import { Suspense, lazy } from "react";
import { createPortal } from "react-dom";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { Toaster } from "sonner";
import { MainLayout } from "@/components/layout/MainLayout";
import { AdminGuard } from "@/features/auth/guards/AdminGuard";
import { AuthGuard } from "@/features/auth/guards/AuthGuard";
import { CredentialsRouteGuard } from "@/features/auth/guards/CredentialsRouteGuard";
import { GuestGuard } from "@/features/auth/guards/GuestGuard";
import { MfaChallengeGuard } from "@/features/auth/guards/MfaChallengeGuard";
import { MfaEnrollGuard } from "@/features/auth/guards/MfaEnrollGuard";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";

// Lazy load pages
const LoginPage = lazy(() => import("@/features/auth/LoginPage"));
const RegisterPage = lazy(() => import("@/features/auth/RegisterPage"));
const DashboardIndex = lazy(
  () => import("@/features/dashboard/DashboardIndex"),
);
const BillingPage = lazy(() => import("@/features/billing/BillingPage"));
const CostAnalysisPage = lazy(
  () => import("@/features/costs/CostAnalysisPage"),
);
const MarginRoute = lazy(() => import("@/features/margin/MarginRoute"));
const BillExplorerPage = lazy(
  () => import("@/features/costs/BillExplorerPage"),
);
const InventoryPage = lazy(() => import("@/features/inventory/InventoryPage"));
const InventoryResourcePage = lazy(
  () => import("@/features/inventory/InventoryResourcePage"),
);
const CostServicePage = lazy(() => import("@/features/costs/CostServicePage"));
const DeskHomePage = lazy(() => import("@/features/desk/DeskHomePage"));
import { DeskSurfaceGuard } from "@/features/desk/DeskSurfaceGuard";
import { DeskAccessGate } from "@/features/desk/DeskAccessGate";
const DeskReportsPage = lazy(() => import("@/features/desk/DeskReportsPage"));
const MyWorkPage = lazy(() => import("@/features/desk/MyWorkPage"));
const EscalationsPage = lazy(() => import("@/features/desk/EscalationsPage"));
const OverduePage = lazy(() => import("@/features/desk/OverduePage"));
const DeskTicketPage = lazy(() => import("@/features/desk/DeskTicketPage"));
const DeskPage = lazy(() => import("@/features/desk/DeskPage"));
const DeskAutomationPage = lazy(
  () => import("@/features/desk/DeskAutomationPage"),
);
const DeskRunPage = lazy(() => import("@/features/desk/DeskRunPage"));
const DeskTeamsPage = lazy(() => import("@/features/desk/DeskTeamsPage"));
const DeskCampaignsPage = lazy(
  () => import("@/features/desk/DeskCampaignsPage"),
);
const SchedulingPage = lazy(
  () => import("@/features/scheduling/SchedulingPage"),
);
const ScheduleDetailPage = lazy(
  () => import("@/features/scheduling/ScheduleDetailPage"),
);
const RecommendationsPage = lazy(
  () => import("@/features/recommendations/RecommendationsPage"),
);
const CredentialsPage = lazy(
  () => import("@/features/credentials/CredentialsPage"),
);
const ReportsPage = lazy(() => import("@/features/reports/ReportsPage"));
const AlertsPage = lazy(() => import("@/features/alerts/AlertsPage"));
const FinOpsCapabilitiesPage = lazy(
  () => import("@/features/capabilities/FinOpsCapabilitiesPage"),
);
const SettingsPage = lazy(() => import("@/features/settings/SettingsPage"));
const AdminPage = lazy(() => import("@/features/admin/AdminPage"));
const PendingApprovalPage = lazy(
  () => import("@/features/onboarding/PendingApprovalPage"),
);
const RegistrationRejectedPage = lazy(
  () => import("@/features/onboarding/RegistrationRejectedPage"),
);
const InviteAcceptPage = lazy(
  () => import("@/features/onboarding/InviteAcceptPage"),
);
const SSOCallbackPage = lazy(() => import("@/features/auth/SSOCallbackPage"));
const PasswordResetRequestPage = lazy(
  () => import("@/features/auth/PasswordResetRequestPage"),
);
const PasswordResetConfirmPage = lazy(
  () => import("@/features/auth/PasswordResetConfirmPage"),
);
const EmailVerifyPage = lazy(() => import("@/features/auth/EmailVerifyPage"));
const EmailChangeConfirmPage = lazy(
  () => import("@/features/auth/EmailChangeConfirmPage"),
);
const MfaEnrollPage = lazy(() => import("@/features/auth/MfaEnrollPage"));
const MfaChallengePage = lazy(() => import("@/features/auth/MfaChallengePage"));
const MfaRecoveryCodesPage = lazy(
  () => import("@/features/auth/MfaRecoveryCodesPage"),
);
const MfaDisablePage = lazy(() => import("@/features/auth/MfaDisablePage"));
const ApprovalsPage = lazy(() => import("@/features/admin/ApprovalsPage"));

/** A retired cost route, sent to the page that absorbed it with its query kept. */
function ToAlert() {
  const { id } = useParams();
  return <Navigate to={`/alerts?tab=history&alert=${id ?? ""}`} replace />;
}

function ToBillExplorer() {
  const { search } = useLocation();
  return <Navigate to={`/costs/explorer${search}`} replace />;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Auth routes — each lazy page has its own Suspense so the whole tree does not suspend */}
        <Route
          path="/login"
          element={
            <Suspense fallback={<LoadingSpinner fullScreen />}>
              <GuestGuard>
                <LoginPage />
              </GuestGuard>
            </Suspense>
          }
        />
        <Route
          path="/register"
          element={
            <Suspense fallback={<LoadingSpinner fullScreen />}>
              <GuestGuard>
                <RegisterPage />
              </GuestGuard>
            </Suspense>
          }
        />
        {/* MSP sign-up merged into /register. Redirect rather than 404 — this
            URL has been circulated in distributor onboarding emails. */}
        <Route
          path="/onboard/msp"
          element={<Navigate to="/register" replace />}
        />
        {/* No `GuestGuard`. It sends an authenticated visitor to the dashboard,
            which is right for `/login` and wrong here: an invitation is opened
            from an email in whatever browser the reader has, often one where
            somebody never signed out, and closing a tab does not end a session.
            Bouncing them returned them to the wrong account and made the link
            look broken. The page handles the conflict itself, because only the
            server can say whether the invitation is this reader's. */}
        <Route
          path="/invite/:token"
          element={
            <Suspense fallback={<LoadingSpinner fullScreen />}>
              <InviteAcceptPage />
            </Suspense>
          }
        />
        <Route
          path="/registration-rejected"
          element={
            <AuthGuard>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <RegistrationRejectedPage />
              </Suspense>
            </AuthGuard>
          }
        />
        <Route
          path="/pending-approval"
          element={
            <AuthGuard>
              <Suspense fallback={<LoadingSpinner fullScreen />}>
                <PendingApprovalPage />
              </Suspense>
            </AuthGuard>
          }
        />
        <Route
          path="/auth/callback"
          element={
            <Suspense fallback={<LoadingSpinner fullScreen />}>
              <SSOCallbackPage />
            </Suspense>
          }
        />
        <Route
          path="/auth/password-reset"
          element={
            <Suspense fallback={<LoadingSpinner fullScreen />}>
              <PasswordResetRequestPage />
            </Suspense>
          }
        />
        <Route
          path="/auth/password-reset/confirm"
          element={
            <Suspense fallback={<LoadingSpinner fullScreen />}>
              <PasswordResetConfirmPage />
            </Suspense>
          }
        />
        <Route
          path="/auth/email-verify"
          element={
            <Suspense fallback={<LoadingSpinner fullScreen />}>
              <EmailVerifyPage />
            </Suspense>
          }
        />
        <Route
          path="/auth/email-change/confirm"
          element={
            <Suspense fallback={<LoadingSpinner fullScreen />}>
              <EmailChangeConfirmPage />
            </Suspense>
          }
        />
        <Route
          path="/auth/mfa/enroll"
          element={
            <Suspense fallback={<LoadingSpinner fullScreen />}>
              <MfaEnrollGuard>
                <MfaEnrollPage />
              </MfaEnrollGuard>
            </Suspense>
          }
        />
        <Route
          path="/auth/mfa/challenge"
          element={
            <Suspense fallback={<LoadingSpinner fullScreen />}>
              <MfaChallengeGuard>
                <MfaChallengePage />
              </MfaChallengeGuard>
            </Suspense>
          }
        />
        <Route
          path="/auth/mfa/recovery-codes"
          element={
            <Suspense fallback={<LoadingSpinner fullScreen />}>
              <AuthGuard>
                <MfaRecoveryCodesPage />
              </AuthGuard>
            </Suspense>
          }
        />
        <Route
          path="/auth/mfa/disable"
          element={
            <Suspense fallback={<LoadingSpinner fullScreen />}>
              <AuthGuard>
                <MfaDisablePage />
              </AuthGuard>
            </Suspense>
          }
        />

        {/* Protected routes — lazy pages suspend inside MainLayout’s <Outlet /> only */}
        <Route
          element={
            <AuthGuard>
              <MainLayout />
            </AuthGuard>
          }
        >
          <Route index element={<DashboardIndex />} />
          {/* Billing sits beside the dashboard: statements per child
              organization for resellers. Behind the invoices subsystem gate
              until an invoicing API exists; the demo logins see statements
              built from their real organizations. */}
          <Route path="billing" element={<BillingPage />} />
          <Route path="costs" element={<CostAnalysisPage />} />
          {/* The bill, grouped by any dimension, with margin beside it for
              resellers. Cost Analysis at /costs is a separate page. */}
          <Route path="costs/explorer" element={<BillExplorerPage />} />
          <Route path="costs/by-organization" element={<ToBillExplorer />} />
          {/* By organization and Breakdown were folded into the Bill Explorer.
              Old links land there with their query kept. */}
          <Route path="costs/breakdown" element={<ToBillExplorer />} />
          <Route path="costs/margin" element={<MarginRoute />} />
          {/* The service at full width: the same view the cost page's panel
              renders, at the other of its two URLs. */}
          <Route path="costs/services/:id" element={<CostServicePage />} />
          <Route path="inventory" element={<InventoryPage />} />
          {/* The resource at full width: the same view the table's panel
              renders, at the other of its two URLs. */}
          <Route
            path="inventory/resources/:id"
            element={<InventoryResourcePage />}
          />
          <Route path="scheduling" element={<SchedulingPage />} />
          <Route path="scheduling/:id" element={<ScheduleDetailPage />} />
          <Route path="recommendations" element={<RecommendationsPage />} />
          {/* Recommendations stays a source, not a destination. Desk is where
              a finding becomes work.

              Desk is a set of surfaces rather than one page: the overview
              answers "is this earning its keep", the queue answers "what should
              I pick up", and runs and sources are the machinery behind both.
              Its rail navigates between them. */}
          <Route
            path="desk"
            element={
              <DeskAccessGate>
                <DeskHomePage />
              </DeskAccessGate>
            }
          />
          <Route
            path="desk/reports"
            element={
              <DeskAccessGate>
                <DeskReportsPage />
              </DeskAccessGate>
            }
          />
          <Route
            path="desk/my-work"
            element={
              <DeskAccessGate>
                <MyWorkPage />
              </DeskAccessGate>
            }
          />
          <Route
            path="desk/escalations"
            element={
              <DeskSurfaceGuard surface="escalations">
                <EscalationsPage />
              </DeskSurfaceGuard>
            }
          />
          <Route
            path="desk/overdue"
            element={
              <DeskSurfaceGuard surface="overdue">
                <OverduePage />
              </DeskSurfaceGuard>
            }
          />
          <Route
            path="desk/queue"
            element={
              <DeskSurfaceGuard surface="queue">
                <DeskPage />
              </DeskSurfaceGuard>
            }
          />
          <Route
            path="desk/tickets/:id"
            element={
              <DeskAccessGate>
                <DeskTicketPage />
              </DeskAccessGate>
            }
          />
          <Route
            path="desk/automation"
            element={
              <DeskSurfaceGuard surface="automation">
                <DeskAutomationPage />
              </DeskSurfaceGuard>
            }
          />
          <Route
            path="desk/automation/runs/:id"
            element={
              <DeskSurfaceGuard surface="automation">
                <DeskRunPage />
              </DeskSurfaceGuard>
            }
          />
          <Route
            path="desk/runs"
            element={<Navigate to="/desk/automation" replace />}
          />
          <Route
            path="desk/sources"
            element={<Navigate to="/desk/automation" replace />}
          />
          <Route
            path="desk/teams"
            element={
              <DeskSurfaceGuard surface="teams">
                <DeskTeamsPage />
              </DeskSurfaceGuard>
            }
          />
          <Route
            path="desk/campaigns"
            element={
              <DeskSurfaceGuard surface="campaigns">
                <DeskCampaignsPage />
              </DeskSurfaceGuard>
            }
          />
          <Route
            path="credentials"
            element={
              <CredentialsRouteGuard>
                <CredentialsPage />
              </CredentialsRouteGuard>
            }
          />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="alerts" element={<AlertsPage />} />
          {/* The in-app notification links here; the alert opens on the page. */}
          <Route path="alerts/:id" element={<ToAlert />} />
          <Route path="capabilities" element={<FinOpsCapabilitiesPage />} />
          <Route path="settings/*" element={<SettingsPage />} />
          <Route
            path="admin/*"
            element={
              <AdminGuard>
                <AdminPage />
              </AdminGuard>
            }
          />
          <Route
            path="admin/approvals"
            element={
              <AdminGuard>
                <ApprovalsPage />
              </AdminGuard>
            }
          />
          {/* Retired in scope-filter-vs-write-target fix (S5a):
              legacy InvitationsPage hard-coded role + msp from authStore.
              The AdminPage users view already has an Invite User button that
              uses the shared InviteUserDialog + OrgTargetPicker. */}
          <Route
            path="admin/invitations"
            element={
              <AdminGuard>
                <Navigate to="/admin/users" replace />
              </AdminGuard>
            }
          />
          {/* Transfer is now a modal inside the Organizations detail; keep the old
              deep-link working by redirecting to it. */}
          <Route
            path="admin/companies/:companyId/transfer"
            element={<Navigate to="/admin/organizations" replace />}
          />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {/* Portalled to `body`, and that is load-bearing rather than tidy.
          Sonner does not portal: it renders wherever it is mounted, which here
          is inside `#root`. `globals.css` gives `body > #root` a `z-index: 1`
          so the ambient wash sits behind the app — and that makes #root a
          stacking context, capping everything inside it below any body-level
          sibling. Radix portals every dialog and sheet to `body` with `z-50`,
          so a toast raised to sonner's own `z-index: 999999999` still rendered
          *behind* an open modal: 999999999 inside a layer worth 1 loses to 50
          beside it. Mounting the toaster as a body sibling puts it back in the
          same stacking context as the overlays it has to sit above.

          `richColors` is dropped deliberately: it paints toasts from sonner's
          own palette, which sits outside the token system and ignores the
          theme. These class overrides route every toast surface back through
          our tokens. */}
      {createPortal(
        <Toaster
          position="top-right"
          toastOptions={{
            classNames: {
              toast:
                "bg-card text-card-foreground border border-border shadow-lg rounded-md",
              title: "font-display font-semibold",
              description: "text-muted-foreground",
              actionButton: "bg-primary text-primary-foreground",
              cancelButton: "bg-muted text-muted-foreground",
              success: "text-success",
              error: "text-destructive",
              warning: "text-warning",
              info: "text-info",
            },
          }}
        />,
        document.body,
      )}
    </BrowserRouter>
  );
}

export default App;
